import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Chat, PaperPlaneTilt, Spinner, At, CaretRight,
  PushPin, Clock, Eye, PencilSimple, CheckCircle,
  Pencil, Trash, ArrowCounterClockwise, X, Microphone, Sparkle,
} from '@phosphor-icons/react';
import { api } from '../../lib/api-client';
import { useSubmissionStore } from '../../stores/submissionStore';
import { useReviewStore, type TeamMember } from '../../stores/reviewStore';
import { useAuthStore } from '../../stores/authStore';
import type { Submission, SubmissionComment, SubmissionStatus } from '@spine-workspace/shared';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(iso: string) {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return new Date(iso).toLocaleDateString('en', { month: 'short', day: 'numeric' });
}

const VERSION_PALETTE = [
  { dot: 'bg-blue-500',    tab: 'bg-blue-500 text-white',    ring: 'ring-blue-500/30'   },
  { dot: 'bg-violet-500',  tab: 'bg-violet-500 text-white',  ring: 'ring-violet-500/30' },
  { dot: 'bg-emerald-500', tab: 'bg-emerald-500 text-white', ring: 'ring-emerald-500/30'},
  { dot: 'bg-amber-500',   tab: 'bg-amber-500 text-white',   ring: 'ring-amber-500/30'  },
  { dot: 'bg-rose-500',    tab: 'bg-rose-500 text-white',    ring: 'ring-rose-500/30'   },
  { dot: 'bg-teal-500',    tab: 'bg-teal-500 text-white',    ring: 'ring-teal-500/30'   },
];

function palette(v: number) {
  return VERSION_PALETTE[(v - 1) % VERSION_PALETTE.length];
}

const STATUS_META: Record<SubmissionStatus, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  waiting_for_review: { label: 'Waiting', color: 'text-amber-400',   bg: 'bg-amber-400/10',   icon: <Clock size={9} weight="fill" /> },
  in_review:          { label: 'In Review', color: 'text-blue-400',  bg: 'bg-blue-400/10',    icon: <Eye size={9} weight="fill" /> },
  need_update:        { label: 'Needs Update', color: 'text-red-400',bg: 'bg-red-400/10',     icon: <PencilSimple size={9} weight="fill" /> },
  approved:           { label: 'Approved', color: 'text-emerald-400',bg: 'bg-emerald-400/10', icon: <CheckCircle size={9} weight="fill" /> },
  cancelled:          { label: 'Cancelled', color: 'text-text-tertiary', bg: 'bg-bg-input',   icon: null },
};

/** Render a message string, highlighting @mentions as accent chips */
function renderMessage(message: string) {
  const parts = message.split(/(@[\w.]+)/g);
  return parts.map((part, i) =>
    /^@[\w.]/.test(part) ? (
      <span key={i} className="text-accent font-semibold bg-accent/10 rounded px-0.5 py-px">
        {part}
      </span>
    ) : (
      part
    )
  );
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function Avatar({ name = '?', url, size = 22 }: { name?: string; url?: string; size?: number }) {
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        className="rounded-full object-cover shrink-0 ring-1 ring-border"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-accent/20 flex items-center justify-center shrink-0 text-accent font-mono font-bold uppercase ring-1 ring-accent/20"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
    >
      {name[0]}
    </div>
  );
}

// ─── CommentBubble ────────────────────────────────────────────────────────────

function CommentBubble({ comment, isOwn }: { comment: SubmissionComment; isOwn: boolean }) {
  const hasPin = !!comment.annotationId;
  const [highlighted, setHighlighted] = useState(false);

  function handlePinClick() {
    if (!comment.annotationId) return;
    // Highlight the annotation pin on canvas by dispatching a custom event
    window.dispatchEvent(new CustomEvent('review:highlight-annotation', {
      detail: { annotationId: comment.annotationId },
    }));
    setHighlighted(true);
    setTimeout(() => setHighlighted(false), 1200);
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
      className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}
    >
      <Avatar name={comment.userName} url={comment.userAvatar} size={20} />
      <div className={`flex flex-col gap-0.5 min-w-0 max-w-[82%] ${isOwn ? 'items-end' : 'items-start'}`}>
        <div className={`flex items-center gap-1.5 ${isOwn ? 'flex-row-reverse' : ''}`}>
          <span className="text-[9px] font-mono text-text-tertiary font-semibold truncate max-w-[80px]">
            {isOwn ? 'you' : (comment.userName ?? 'Unknown')}
          </span>
          {hasPin && (
            <button
              onClick={handlePinClick}
              title="Jump to canvas annotation"
              className={`flex items-center gap-0.5 text-[8px] font-mono transition-colors ${
                highlighted ? 'text-accent' : 'text-text-tertiary/50 hover:text-accent'
              }`}
            >
              <PushPin size={7} weight={highlighted ? 'fill' : 'regular'} />
            </button>
          )}
          <span className="text-[8px] text-text-tertiary/40 font-mono shrink-0">{timeAgo(comment.createdAt)}</span>
        </div>
        <div
          className={`px-3 py-2 rounded-2xl text-[11px] font-sans leading-relaxed break-words select-text transition-colors ${
            isOwn
              ? 'bg-accent/15 text-accent rounded-tr-none'
              : 'bg-bg-input text-text-primary rounded-tl-none'
          } ${highlighted ? 'ring-1 ring-accent/40' : ''}`}
        >
          {renderMessage(comment.message)}
        </div>
      </div>
    </motion.div>
  );
}

// ─── ReviewDrawControls ───────────────────────────────────────────────────────

const DRAW_COLORS = [
  { color: '#f87171', label: 'Red' },
  { color: '#fb923c', label: 'Orange' },
  { color: '#facc15', label: 'Yellow' },
  { color: '#4ade80', label: 'Green' },
  { color: '#60a5fa', label: 'Blue' },
];

function ReviewDrawControls({ version }: { version: number }) {
  const {
    drawingVersion, reviewDrawColor, reviewStrokes,
    startReviewDraw, stopReviewDraw, setReviewDrawColor,
    undoReviewStroke, clearReviewStrokes,
  } = useReviewStore();
  const isActive = drawingVersion === version;
  const strokeCount = reviewStrokes[version]?.length ?? 0;

  if (!isActive && strokeCount === 0) {
    return (
      <button
        onClick={() => startReviewDraw(version)}
        className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-mono text-text-tertiary hover:text-accent hover:bg-accent/10 transition-colors"
        title={`Draw on version ${version}`}
      >
        <Pencil size={9} />
        Draw
      </button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`flex items-center gap-1 px-1.5 py-1 rounded-lg border transition-colors ${
        isActive ? 'bg-bg-input border-accent/30' : 'bg-bg-input border-border-subtle'
      }`}
    >
      {isActive && (
        <>
          {/* Color swatches */}
          {DRAW_COLORS.map((dc) => (
            <button
              key={dc.color}
              onClick={() => setReviewDrawColor(dc.color)}
              title={dc.label}
              className={`w-3 h-3 rounded-full shrink-0 transition-transform hover:scale-125 ${
                reviewDrawColor === dc.color ? 'ring-2 ring-white ring-offset-1 ring-offset-bg-input' : ''
              }`}
              style={{ backgroundColor: dc.color }}
            />
          ))}
          <div className="w-px h-3 bg-border-subtle mx-0.5" />
        </>
      )}
      {/* Undo */}
      <button
        onClick={() => undoReviewStroke(version)}
        disabled={strokeCount === 0}
        className="w-5 h-5 flex items-center justify-center rounded text-text-tertiary hover:text-accent hover:bg-accent/10 disabled:opacity-30 transition-colors"
        title="Undo last stroke"
      >
        <ArrowCounterClockwise size={9} />
      </button>
      {/* Clear */}
      <button
        onClick={() => clearReviewStrokes(version)}
        disabled={strokeCount === 0}
        className="w-5 h-5 flex items-center justify-center rounded text-text-tertiary hover:text-red-400 hover:bg-red-400/10 disabled:opacity-30 transition-colors"
        title="Clear all strokes"
      >
        <Trash size={9} />
      </button>
      {/* Toggle draw mode */}
      <button
        onClick={() => isActive ? stopReviewDraw() : startReviewDraw(version)}
        className={`w-5 h-5 flex items-center justify-center rounded transition-colors ${
          isActive
            ? 'text-accent bg-accent/15 hover:bg-accent/25'
            : 'text-text-tertiary hover:text-accent hover:bg-accent/10'
        }`}
        title={isActive ? 'Exit draw mode' : 'Enter draw mode'}
      >
        {isActive ? <X size={9} /> : <Pencil size={9} />}
      </button>
    </motion.div>
  );
}

// ─── VersionSection ───────────────────────────────────────────────────────────

function VersionSection({
  version,
  comments,
  currentUserId,
  defaultOpen = true,
}: {
  version: number | null;
  comments: SubmissionComment[];
  currentUserId?: string;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const pal = version != null ? palette(version) : null;
  const label = version != null ? `Version ${version}` : 'General';

  return (
    <div>
      {/* Section header */}
      <div className="flex items-center gap-2 py-1.5 group">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 flex-1 min-w-0 select-none"
        >
          <div className={`w-2 h-2 rounded-full shrink-0 ${pal?.dot ?? 'bg-text-tertiary/40'}`} />
          <span className="text-[9px] font-mono font-semibold text-text-tertiary uppercase tracking-widest flex-1 text-left truncate">
            {label}
          </span>
          <span className="text-[8px] font-mono text-text-tertiary/40 shrink-0">{comments.length}</span>
          <motion.span animate={{ rotate: open ? 90 : 0 }} transition={{ duration: 0.15 }} className="shrink-0">
            <CaretRight size={8} className="text-text-tertiary/40 group-hover:text-text-tertiary transition-colors" />
          </motion.span>
        </button>
        {/* Draw controls — only for real version numbers */}
        {version != null && <ReviewDrawControls version={version} />}
      </div>

      {/* Comments */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="pl-3 pb-3 space-y-3 border-l border-border-subtle ml-1">
              {comments.map((c) => (
                <CommentBubble key={c.id} comment={c} isOwn={c.userId === currentUserId} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Mention handle builder ───────────────────────────────────────────────────

/** Returns a unique short handle for a user within a list.
 *  Uses first name when unique; adds last-name initial(s) to disambiguate. */
function buildHandle(user: TeamMember, allUsers: TeamMember[]): string {
  const parts = user.name.trim().split(/\s+/);
  const first = parts[0];
  // Check if any other user shares the same first name
  const clash = allUsers.some((u) => u.id !== user.id && u.name.trim().split(/\s+/)[0].toLowerCase() === first.toLowerCase());
  if (!clash) return first;
  // Add subsequent initials until unique
  let handle = first;
  for (let i = 1; i < parts.length; i++) {
    handle += parts[i][0].toUpperCase();
    const stillClash = allUsers.some(
      (u) => u.id !== user.id && buildHandleRaw(u.name, i + 1).toLowerCase() === handle.toLowerCase()
    );
    if (!stillClash) break;
  }
  return handle;
}

function buildHandleRaw(name: string, wordCount: number): string {
  const parts = name.trim().split(/\s+/);
  if (wordCount <= 1) return parts[0];
  return parts[0] + parts.slice(1, wordCount).map((p) => p[0].toUpperCase()).join('');
}

// ─── MentionDropdown ──────────────────────────────────────────────────────────

function MentionDropdown({
  query,
  users,
  onSelect,
}: {
  query: string;
  users: TeamMember[];
  onSelect: (u: TeamMember) => void;
}) {
  const filtered = users
    .filter((u) => {
      const handle = buildHandle(u, users).toLowerCase();
      return (
        u.name.toLowerCase().includes(query.toLowerCase()) ||
        handle.startsWith(query.toLowerCase())
      );
    })
    .slice(0, 6);

  if (filtered.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 6, scale: 0.97 }}
      transition={{ duration: 0.12 }}
      className="absolute bottom-full mb-2 left-0 right-0 bg-bg-elevated border border-border rounded-xl shadow-2xl overflow-hidden z-50"
    >
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-border-subtle">
        <At size={10} className="text-accent" />
        <span className="text-[9px] font-mono text-text-tertiary tracking-wide">Mention teammate</span>
      </div>
      {filtered.map((u) => (
        <button
          key={u.id}
          onMouseDown={(e) => { e.preventDefault(); onSelect(u); }}
          className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-bg-hover transition-colors text-left"
        >
          <Avatar name={u.name} url={u.avatarUrl} size={18} />
          <span className="text-[11px] font-sans text-text-primary">{u.name}</span>
          <span className="text-[9px] font-mono text-accent ml-auto opacity-60">@{buildHandle(u, users)}</span>
        </button>
      ))}
    </motion.div>
  );
}

// ─── CommentInput ─────────────────────────────────────────────────────────────

function CommentInput({
  submissionId,
  activeVersion,
  sceneId,
}: {
  submissionId: string;
  activeVersion: number | 'all';
  sceneId?: string;
}) {
  const { addComment } = useSubmissionStore();
  const { teamMembers, fetchTeamMembers } = useReviewStore();
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionStart, setMentionStart] = useState(-1);
  const [isRecording, setIsRecording] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recognitionRef = useRef<any>(null);
  const recordingBaseRef = useRef('');

  useEffect(() => {
    fetchTeamMembers(sceneId);
  }, [sceneId]); // eslint-disable-line react-hooks/exhaustive-deps

  const versionNumber = activeVersion === 'all' ? undefined : activeVersion;
  const pal = versionNumber != null ? palette(versionNumber) : null;

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const val = e.target.value;
    setDraft(val);
    // Detect @mention trigger
    const cursor = e.target.selectionStart ?? 0;
    const upTo = val.slice(0, cursor);
    const match = upTo.match(/@([\w.]*)$/);
    if (match) {
      setMentionQuery(match[1]);
      setMentionStart(cursor - match[0].length);
    } else {
      setMentionQuery(null);
    }
  }

  function handleMentionSelect(u: TeamMember) {
    const handle = buildHandle(u, teamMembers);
    const before = draft.slice(0, mentionStart);
    const after = draft.slice(mentionStart + 1 + (mentionQuery?.length ?? 0));
    setDraft(`${before}@${handle} ${after}`);
    setMentionQuery(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function toggleRecording() {
    if (isRecording) {
      recognitionRef.current?.stop();
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    const SpeechRecognitionAPI = w.SpeechRecognition ?? w.webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) return;

    recordingBaseRef.current = draft;
    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onresult = (event: any) => {
      let final = '';
      let interim = '';
      for (let i = 0; i < event.results.length; i++) {
        if (event.results[i].isFinal) final += event.results[i][0].transcript;
        else interim += event.results[i][0].transcript;
      }
      const base = recordingBaseRef.current;
      const sep = base && !base.endsWith(' ') ? ' ' : '';
      setDraft(base + sep + final + interim);
    };

    recognition.onend = () => setIsRecording(false);
    recognition.onerror = () => setIsRecording(false);

    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
  }

  async function handlePolish() {
    const raw = draft.trim();
    if (!raw || polishing) return;
    setPolishing(true);
    try {
      const { polished } = await api.post<{ polished: string }>('/api/ai/polish', { text: raw });
      setDraft(polished);
    } catch {
      // keep original draft on error
    } finally {
      setPolishing(false);
    }
  }

  async function handleSend() {
    const msg = draft.trim();
    if (!msg || sending) return;
    setSending(true);
    try {
      await addComment(submissionId, msg, undefined, versionNumber);
      setDraft('');
      setMentionQuery(null);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="px-3 py-3 border-t border-border-subtle shrink-0 relative">
      <AnimatePresence>
        {mentionQuery !== null && (
          <MentionDropdown
            key="mention"
            query={mentionQuery}
            users={teamMembers}
            onSelect={handleMentionSelect}
          />
        )}
      </AnimatePresence>

      {/* Version tag */}
      {pal && versionNumber != null && (
        <div className="flex items-center gap-1.5 mb-2">
          <div className={`w-1.5 h-1.5 rounded-full ${pal.dot}`} />
          <span className="text-[9px] font-mono text-text-tertiary">
            Commenting on version {versionNumber}
          </span>
        </div>
      )}

      <div className="flex gap-2 items-end">
        <textarea
          ref={inputRef}
          value={draft}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (mentionQuery !== null) return; // wait for mention selection
              handleSend();
            }
            if (e.key === 'Escape') setMentionQuery(null);
            e.stopPropagation();
          }}
          placeholder="Comment… (@ to mention)"
          rows={1}
          className="flex-1 min-w-0 bg-bg-input border border-border rounded-xl px-3 py-2 text-[11px] font-sans text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent transition-colors resize-none overflow-hidden"
          style={{ lineHeight: '1.5', maxHeight: 80 }}
          onInput={(e) => {
            // Auto-grow textarea
            const t = e.currentTarget;
            t.style.height = 'auto';
            t.style.height = Math.min(t.scrollHeight, 80) + 'px';
          }}
        />
        <button
          onClick={toggleRecording}
          title={isRecording ? 'Stop recording' : 'Voice to text'}
          className={`shrink-0 w-8 h-8 flex items-center justify-center rounded-xl transition-all active:scale-95 ${
            isRecording
              ? 'bg-rose-500/20 text-rose-400 animate-pulse'
              : 'bg-bg-input border border-border text-text-tertiary hover:text-text-primary hover:border-accent/50'
          }`}
        >
          <Microphone size={13} weight={isRecording ? 'fill' : 'regular'} />
        </button>
        <button
          onClick={handlePolish}
          disabled={!draft.trim() || polishing || isRecording}
          title="Polish with AI"
          className="shrink-0 w-8 h-8 flex items-center justify-center rounded-xl bg-violet-500/10 text-violet-400 hover:bg-violet-500/20 disabled:opacity-40 transition-all active:scale-95"
        >
          {polishing ? (
            <Spinner size={11} className="animate-spin" />
          ) : (
            <Sparkle size={13} weight="fill" />
          )}
        </button>
        <button
          onClick={handleSend}
          disabled={!draft.trim() || sending}
          className="shrink-0 w-8 h-8 flex items-center justify-center rounded-xl bg-accent/15 text-accent hover:bg-accent/25 disabled:opacity-40 transition-all active:scale-95"
        >
          {sending ? (
            <Spinner size={11} className="animate-spin" />
          ) : (
            <PaperPlaneTilt size={13} weight="fill" />
          )}
        </button>
      </div>
      <p className="text-[8px] font-mono text-text-tertiary/50 mt-1.5">
        {isRecording
          ? '🔴 Recording… click mic to stop'
          : polishing
            ? '✨ Polishing with AI…'
            : 'Enter · Shift+Enter · @ mention · 🎤 record · ✨ polish'}
      </p>
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyFeed() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="flex flex-col items-center justify-center py-12 px-6 text-center"
    >
      <div className="text-4xl mb-3 select-none" style={{ filter: 'grayscale(0.3)' }}>
        💬
      </div>
      <p className="text-[11px] font-sans text-text-tertiary font-medium">No feedback yet</p>
      <p className="text-[9px] font-sans text-text-tertiary/60 mt-1 leading-relaxed">
        Be the first to leave a comment.<br />Use @ to mention a teammate.
      </p>
    </motion.div>
  );
}

// ─── Status Action Bar ────────────────────────────────────────────────────────

function StatusActionBar({ submission }: { submission: Submission }) {
  const { updateStatus } = useSubmissionStore();
  const isAdmin = useAuthStore((s) => s.user?.isAdmin ?? false);
  const [acting, setActing] = useState(false);

  if (!isAdmin) return null;

  async function act(status: SubmissionStatus, notes?: string) {
    setActing(true);
    try { await updateStatus(submission.id, status, notes); }
    finally { setActing(false); }
  }

  const { status } = submission;

  return (
    <div className="px-3 py-2 border-b border-border-subtle shrink-0 flex flex-col gap-1.5">
      {status === 'waiting_for_review' && (
        <button
          disabled={acting}
          onClick={() => act('in_review')}
          className="w-full py-1.5 text-[11px] font-sans rounded-lg bg-blue-400/10 text-blue-400 hover:bg-blue-400/20 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
        >
          {acting ? <Spinner size={10} className="animate-spin" /> : <Eye size={11} />}
          Start Review
        </button>
      )}
      {status === 'in_review' && (
        <div className="flex gap-1.5">
          <button
            disabled={acting}
            onClick={() => act('approved')}
            className="flex-1 py-1.5 text-[11px] font-sans rounded-lg bg-emerald-400/10 text-emerald-400 hover:bg-emerald-400/20 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
          >
            {acting ? <Spinner size={10} className="animate-spin" /> : <CheckCircle size={11} />}
            Approve
          </button>
          <button
            disabled={acting}
            onClick={() => act('need_update')}
            className="flex-1 py-1.5 text-[11px] font-sans rounded-lg bg-red-400/10 text-red-400 hover:bg-red-400/20 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
          >
            {acting ? <Spinner size={10} className="animate-spin" /> : <PencilSimple size={11} />}
            Needs Update
          </button>
        </div>
      )}
      {status === 'need_update' && (
        <p className="text-[9px] font-mono text-center text-text-tertiary py-0.5">
          Waiting for submitter to re-upload
        </p>
      )}
      {status === 'approved' && (
        <button
          disabled={acting}
          onClick={() => act('in_review')}
          className="w-full py-1.5 text-[11px] font-sans rounded-lg bg-bg-hover text-text-secondary hover:text-text-primary disabled:opacity-50 transition-colors"
        >
          Recall Approval
        </button>
      )}
      {submission.reviewNotes && (
        <p className="text-[9px] italic text-text-tertiary bg-bg-input rounded-lg px-2 py-1 border border-border">
          "{submission.reviewNotes}"
        </p>
      )}
    </div>
  );
}

// ─── No submission linked ─────────────────────────────────────────────────────

function NoSubmission() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="flex flex-col items-center justify-center h-full px-6 text-center"
    >
      <div className="text-4xl mb-3 select-none opacity-30">🔗</div>
      <p className="text-[11px] font-sans text-text-tertiary font-medium">No review linked</p>
      <p className="text-[9px] font-sans text-text-tertiary/60 mt-1 leading-relaxed">
        Open a review scene to see feedback here.
      </p>
    </motion.div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

export interface ReviewFeedbackPanelProps {
  submissionId: string | null;
  sceneId?: string;
}

export default function ReviewFeedbackPanel({ submissionId, sceneId }: ReviewFeedbackPanelProps) {
  const { submissions, fetchSubmissions, comments, commentsLoading, fetchComments } =
    useSubmissionStore();
  const { activeVersion, setActiveVersion } = useReviewStore();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { drawingVersion, stopReviewDraw } = useReviewStore();

  useEffect(() => {
    fetchSubmissions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!submissionId) return;
    fetchComments(submissionId);
    const timer = setInterval(() => fetchComments(submissionId), 15_000);
    return () => clearInterval(timer);
  }, [submissionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const allComments: SubmissionComment[] = submissionId ? (comments[submissionId] ?? []) : [];
  const isLoading = submissionId ? (commentsLoading[submissionId] ?? false) : false;
  const submission = submissionId ? submissions.find((s) => s.id === submissionId) ?? null : null;

  // Scroll to bottom when new comments arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [allComments.length]);

  const versions = [
    ...new Set(
      allComments
        .map((c) => c.versionNumber)
        .filter((v): v is number => typeof v === 'number')
    ),
  ].sort((a, b) => a - b);

  const filtered =
    activeVersion === 'all'
      ? allComments
      : allComments.filter((c) => c.versionNumber === activeVersion);

  function buildGroups(cmts: SubmissionComment[]) {
    const map = new Map<string, SubmissionComment[]>();
    for (const c of cmts) {
      const key = c.versionNumber != null ? String(c.versionNumber) : 'general';
      const arr = map.get(key) ?? [];
      arr.push(c);
      map.set(key, arr);
    }
    const keys = [...map.keys()].sort((a, b) => {
      if (a === 'general') return 1;
      if (b === 'general') return -1;
      return Number(b) - Number(a);
    });
    return keys.map((k) => ({
      key: k,
      version: k === 'general' ? null : Number(k),
      comments: map.get(k)!,
    }));
  }

  const groups = buildGroups(filtered);
  const statusMeta = submission ? STATUS_META[submission.status] : null;

  return (
    <div className="flex flex-col h-full bg-bg-surface overflow-hidden">
      {/* ── Draw mode banner ── */}
      <AnimatePresence>
        {drawingVersion != null && (
          <motion.div
            key="draw-banner"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden shrink-0"
          >
            <div className="flex items-center gap-2 px-3 py-1.5 bg-accent/10 border-b border-accent/20">
              <Pencil size={10} className="text-accent shrink-0" />
              <span className="text-[9px] font-mono text-accent flex-1">
                Drawing on canvas · v{drawingVersion}
              </span>
              <button onClick={stopReviewDraw} className="text-[8px] font-mono text-accent/70 hover:text-accent underline">
                done
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Header ── */}
      <div className="px-3 py-3 border-b border-border-subtle shrink-0">
        <div className="flex items-center gap-2">
          <Chat size={13} className="text-accent shrink-0" />
          <span className="font-serif text-base font-medium text-accent tracking-[0.01em] flex-1 truncate">
            Review
          </span>
          {isLoading && <Spinner size={9} className="animate-spin text-text-tertiary shrink-0" />}
          {statusMeta && (
            <span className={`flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded ${statusMeta.color} ${statusMeta.bg}`}>
              {statusMeta.icon}
              {statusMeta.label}
            </span>
          )}
        </div>
        {submission && (
          <p className="text-[10px] font-sans text-text-tertiary mt-0.5 truncate">
            {submission.submissionName ?? submission.assetName ?? 'Review'}
            {' · '}
            {submission.submitterName ?? 'Unknown'}
          </p>
        )}
      </div>

      {/* ── No submission linked ── */}
      {!submissionId && <NoSubmission />}

      {submissionId && (
        <>
          {/* ── Status actions ── */}
          {submission && <StatusActionBar submission={submission} />}

          {/* ── Version tabs ── */}
          {versions.length > 0 && (
            <div className="flex items-center gap-1 px-3 py-2 border-b border-border-subtle shrink-0 overflow-x-auto no-scrollbar">
              <button
                onClick={() => setActiveVersion('all')}
                className={`shrink-0 px-2.5 py-0.5 rounded-full text-[9px] font-mono font-semibold transition-all ${
                  activeVersion === 'all'
                    ? 'bg-text-tertiary/20 text-text-primary'
                    : 'text-text-tertiary hover:text-text-secondary hover:bg-bg-hover'
                }`}
              >
                All
              </button>
              {versions.map((v) => {
                const pal = palette(v);
                const isActive = activeVersion === v;
                const count = allComments.filter((c) => c.versionNumber === v).length;
                return (
                  <button
                    key={v}
                    onClick={() => setActiveVersion(v)}
                    className={`shrink-0 flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9px] font-mono font-semibold transition-all ring-0 ${
                      isActive
                        ? `${pal.tab} ring-2 ${pal.ring}`
                        : 'text-text-tertiary hover:text-text-secondary hover:bg-bg-hover'
                    }`}
                  >
                    v{v}
                    {count > 0 && (
                      <span className={`min-w-[14px] h-3.5 px-0.5 rounded-full text-[7px] font-mono flex items-center justify-center ${
                        isActive ? 'bg-white/25 text-white' : 'bg-bg-input text-text-tertiary'
                      }`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* ── Comment feed ── */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-1 min-h-0">
            {filtered.length === 0 && !isLoading && <EmptyFeed />}
            {activeVersion === 'all' ? (
              <div className="space-y-2">
                {groups.map((g, i) => (
                  <VersionSection
                    key={g.key}
                    version={g.version}
                    comments={g.comments}
                    currentUserId={currentUserId}
                    defaultOpen={i === 0}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((c) => (
                  <CommentBubble key={c.id} comment={c} isOwn={c.userId === currentUserId} />
                ))}
              </div>
            )}
          </div>

          {/* ── Input ── */}
          <CommentInput submissionId={submissionId} activeVersion={activeVersion} sceneId={sceneId} />
        </>
      )}
    </div>
  );
}
